import {
  Badge,
  cn,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@design-system';

import { Check } from 'lucide-react';

export interface StepInfo {
  id: string;
  isCompleted: boolean;
  title?: string;
}

interface AdvisorOnboardingHeaderProps {
  title?: string;
  currentStepId: string;
  steps: StepInfo[];
  onStepClick?: (stepId: string) => void;
  isLoading?: boolean;
}

export function AdvisorOnboardingHeader({
  title,
  currentStepId,
  steps,
  onStepClick,
  isLoading = false,
}: AdvisorOnboardingHeaderProps) {
  const currentStepIndex = steps.findIndex(s => s.id === currentStepId);

  return (
    <div className="border-border sticky top-0 z-20 border-b bg-white">
      <div className="mx-auto max-w-5xl space-y-4 p-6">
        <div className="flex items-center gap-2 text-sm">
          <span className="font-semibold text-gray-950">{title}</span>
          <span className="text-foreground">•</span>
          <span className="text-foreground">
            Step {currentStepIndex + 1} of {steps.length}
          </span>
        </div>
        <TooltipProvider delayDuration={0}>
          <div className="flex items-center gap-4">
            {steps.map((step, idx) => {
              const isCurrentStep = idx === currentStepIndex;
              const isCompleted = isLoading ? false : step.isCompleted;
              const isFuture = isLoading || (!isCurrentStep && !isCompleted);
              const isPreviousStep = idx < currentStepIndex;
              const canNavigate =
                Boolean(onStepClick) && !isLoading && (isPreviousStep || isCompleted);

              return (
                <Tooltip key={step.id}>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => canNavigate && onStepClick?.(step.id)}
                      disabled={!canNavigate}
                      className={cn('h-1 w-full rounded-full transition-colors duration-300', {
                        'bg-primary/50': !isLoading && isCurrentStep && !isCompleted,
                        'bg-primary': !isLoading && isCompleted,
                        'bg-accent': isFuture,
                        'cursor-pointer hover:opacity-80': canNavigate,
                        'cursor-default': !canNavigate,
                      })}
                    />
                  </TooltipTrigger>
                  <TooltipContent className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{step.title}</span>
                      {!isLoading && isCompleted && (
                        <Badge
                          variant="default"
                          className="bg-primary flex items-center gap-1 text-white"
                        >
                          <Check className="h-3 w-3" />
                          Complete
                        </Badge>
                      )}
                      {!isLoading && isCurrentStep && !isCompleted && (
                        <Badge
                          variant="secondary"
                          className="bg-accent text-gray-950"
                        >
                          Current
                        </Badge>
                      )}
                      {isLoading && (
                        <Badge
                          variant="secondary"
                          className="bg-muted text-muted-foreground"
                        >
                          Loading...
                        </Badge>
                      )}
                    </div>
                  </TooltipContent>
                </Tooltip>
              );
            })}
          </div>
        </TooltipProvider>
      </div>
    </div>
  );
}
